import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'

export const isPincode = (pin) => /^[1-9]\d{5}$/.test(String(pin ?? '').trim())

/** "Delivery by Sat, 11 Oct to New Delhi" from a serviceable estimate. */
function deliveryText(result) {
  const when = result.estimated_date
    ? `by ${new Date(`${result.estimated_date}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}`
    : result.estimated_days ? `in ${result.estimated_days} day${result.estimated_days === 1 ? '' : 's'}` : 'available'
  return `Delivery ${when}${result.city ? ` to ${result.city}` : ''}`
}

/**
 * Shiprocket serviceability line for one PIN (GET /shipping/serviceability) —
 * used by the product page's pincode box and each saved address in the cart.
 * Answers are cached per PIN, so the same PIN in both places costs one call.
 * Failures stay generic: a 503 means Shiprocket isn't reachable/configured,
 * which the shopper can't act on.
 */
export default function DeliveryEstimate({ pincode }) {
  const pin = String(pincode ?? '').trim()
  const { data: result, isPending, isError } = useQuery({
    queryKey: ['delivery-estimate', pin],
    queryFn: () => api.shipping.serviceability(pin).then((r) => r.data),
    enabled: isPincode(pin),
    staleTime: 30 * 60 * 1000,
    retry: false,
  })

  if (!isPincode(pin)) return null

  const line = (className, icon, text) => (
    <span className={`flex items-center gap-1.5 ${className}`}>
      <i className={`fa-solid ${icon}`}></i>
      {text}
    </span>
  )

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] tracking-wider">
      {isPending
        ? line('text-gray-400', 'fa-spinner fa-spin', 'Checking delivery…')
        : isError
          ? line('text-gray-400', 'fa-circle-info', "Couldn't check delivery right now")
          : !result.serviceable
            ? line('text-red-500', 'fa-circle-exclamation', `Sorry, we don't deliver to ${result.pincode} yet`)
            : (
              <>
                {line('text-green-700', 'fa-truck-fast', deliveryText(result))}
                {result.cod_available && line('text-gray-500', 'fa-money-bill-wave', 'COD available')}
              </>
            )}
    </div>
  )
}
